/**
 * /parity/uo_usersearch — 线网 `admin/uo.userSearch/index.html`（用户数据查询）
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  App,
  Button,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
  Table,
  theme,
} from 'antd';
import type { CSSProperties } from 'react';
import { useAuthStore } from '../../store/auth';
import { api, ApiError } from '../../api/client';

const readRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 12,
  padding: '10px 12px',
  background: '#f5f5f5',
  border: '1px solid #e8e8e8',
  borderRadius: 2,
  fontSize: 14,
};

function ReadField({ label, value }: { label: string; value: string | number | null | undefined }) {
  const v = value == null || value === '' ? '—' : String(value);
  return (
    <div style={readRowStyle}>
      <span style={{ width: 200, textAlign: 'right', color: 'rgba(0,0,0,0.85)' }}>{label}</span>
      <span style={{ marginLeft: 8, flex: 1, fontWeight: 500 }}>{v}</span>
    </div>
  );
}

function AccountSearchForm({
  onSearch,
  loading,
  buttonLabel = '查询',
}: {
  onSearch: (account: string) => void;
  loading: boolean;
  buttonLabel?: string;
}) {
  const [form] = Form.useForm();
  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ flex: '200px' }}
      wrapperCol={{ flex: 1 }}
      style={{ maxWidth: 720, marginBottom: 16 }}
      onFinish={(v) => onSearch(String(v.account).trim())}
    >
      <Form.Item
        name="account"
        label="编号ID/账号用户名"
        rules={[{ required: true, message: '请输入编号或用户名' }]}
      >
        <Input placeholder="玩家编号或登录名" />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 200 }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          {buttonLabel}
        </Button>
      </Form.Item>
    </Form>
  );
}

export function ParityUserSearchPage() {
  const { message: msg } = App.useApp();
  const { token } = theme.useToken();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const canUse =
    isSuper ||
    hasMenu('parity.uo_usersearch') ||
    hasNode('parity.uo_usersearch') ||
    hasMenu('platform.deep_search') ||
    hasNode('platform.deep_search') ||
    hasMenu('platform.user.view');

  const [tab1, setTab1] = useState<Record<string, unknown> | null>(null);
  const [tab2, setTab2] = useState<Record<string, unknown> | null>(null);
  const [tab3Node, setTab3Node] = useState<Record<string, unknown> | null>(null);
  const [rpLog, setRpLog] = useState<{ total: number; rows: Record<string, unknown>[] } | null>(
    null,
  );
  const [rpQuery, setRpQuery] = useState<{
    account: string;
    coin: 'rp' | 'tp';
    time: string;
  } | null>(null);
  const [rpPage, setRpPage] = useState(1);
  const [confiscateForm] = Form.useForm();

  const search1Mut = useMutation({
    mutationFn: (account: string) =>
      api.post<Record<string, unknown>>('/api/admin/parity-user-search/search1', { account }),
    onSuccess: (data) => {
      setTab1(data);
      msg.success('查询成功');
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '查询失败'),
  });

  const search2Mut = useMutation({
    mutationFn: (account: string) =>
      api.post<Record<string, unknown>>('/api/admin/parity-user-search/search2', { account }),
    onSuccess: (data) => {
      setTab2(data);
      msg.success('查询成功');
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '查询失败'),
  });

  const search4Mut = useMutation({
    mutationFn: (account: string) =>
      api.post<Record<string, unknown>>('/api/admin/parity-user-search/search4', { account }),
    onSuccess: (data) => {
      setTab3Node(data);
      msg.success('查询成功');
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '查询失败'),
  });

  const search3Mut = useMutation({
    mutationFn: (v: { account: string; uo: string; remark: string }) =>
      api.post<{ php_msg: string }>('/api/admin/parity-user-search/search3', v),
    onSuccess: (data) => {
      msg.success(data.php_msg || '提交成功');
      confiscateForm.resetFields(['uo', 'remark']);
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '提交失败'),
  });

  const rpLogMut = useMutation({
    mutationFn: (params: { account: string; coin: 'rp' | 'tp'; time: string; page: number }) =>
      api.get<{ total: number; rows: Record<string, unknown>[] }>(
        '/api/admin/parity-user-search/rp-tp-log',
        { params: { ...params, limit: 20 } },
      ),
    onSuccess: (data) => setRpLog(data),
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '查询失败'),
  });

  const onConfiscate = () => {
    if (!tab1?.account) {
      msg.warning('请先查询用户');
      return;
    }
    confiscateForm.validateFields().then((v) => {
      Modal.confirm({
        title: '确认罚没捐赠 UO？',
        content: `账户 ${String(tab1.account)}，数量 ${v.uo}`,
        onOk: () =>
          search3Mut.mutateAsync({
            account: String(tab1.account),
            uo: String(v.uo).trim(),
            remark: String(v.remark).trim(),
          }),
      });
    });
  };

  if (!canUse) {
    return <p style={{ color: '#999' }}>无权限：需要 用户数据查询 / 高级搜索 / 用户查看 权限</p>;
  }

  const rpColumns = [
    { title: '序号ID', dataIndex: 'id', width: 90 },
    { title: '编号ID', dataIndex: ['userinfo.number'], width: 100, render: (_: unknown, r: Record<string, unknown>) => r['userinfo.number'] ?? '—' },
    { title: '账号', dataIndex: ['userinfo.account'], width: 120, render: (_: unknown, r: Record<string, unknown>) => r['userinfo.account'] ?? '—' },
    { title: '币种', dataIndex: 'action', width: 80 },
    { title: '变动前', dataIndex: 'before_amount', width: 110 },
    { title: '变动金额', dataIndex: 'amount', width: 110 },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    { title: '对应模型', dataIndex: 'ext_model', width: 140, ellipsis: true },
    { title: '对应Id', dataIndex: 'ext_id', width: 90 },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            inkBarColor: '#16a34a',
            itemActiveColor: '#16a34a',
            itemSelectedColor: '#16a34a',
            titleFontSize: 14,
          },
        },
      }}
    >
      <div style={{ background: '#fff', minHeight: 360, padding: '0 0 16px' }}>
        <Tabs
          type="line"
          defaultActiveKey="t1"
          items={[
            {
              key: 't1',
              label: '个人资产 + 直属团队（直推）资产合计',
              children: (
                <div style={{ maxWidth: 960 }}>
                  <AccountSearchForm
                    onSearch={(a) => search1Mut.mutate(a)}
                    loading={search1Mut.isPending}
                  />
                  {tab1?.notice ? (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: 10,
                        background: token.colorInfoBg,
                        fontSize: 12,
                      }}
                    >
                      {String(tab1.notice)}
                    </div>
                  ) : null}
                  {tab1 ? (
                    <>
                      <ReadField label="个人编号ID" value={tab1.number as string} />
                      <ReadField label="个人账号用户名" value={tab1.account as string} />
                      <ReadField label="个人UO币数量" value={tab1.uo as string} />
                      <ReadField label="冻结UO币数量" value={tab1.uo_freeze as string} />
                      <Form form={confiscateForm} layout="horizontal" labelCol={{ flex: '200px' }} style={{ maxWidth: 720 }}>
                        <Form.Item name="uo" label="罚没捐赠UO数量" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="remark" label="罚没捐赠原因备注" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item wrapperCol={{ offset: 200 }}>
                          <Button type="primary" loading={search3Mut.isPending} onClick={onConfiscate}>
                            提交
                          </Button>
                        </Form.Item>
                      </Form>
                      <ReadField label="个人EP币数量" value={tab1.ep as string} />
                      <ReadField label="个人EP1币数量" value={tab1.ep1 as string} />
                      <ReadField label="个人EP2币数量" value={tab1.ep2 as string} />
                      <ReadField label="个人RP币数量" value={tab1.rp as string} />
                      <ReadField label="个人SP1币数量" value={tab1.sp1 as string} />
                      <ReadField label="个人SP2币数量" value={tab1.sp2 as string} />
                      <ReadField label="个人SP3币数量" value={tab1.sp3 as string} />
                      <ReadField label="个人TP币数量" value={tab1.tp as string} />
                      <ReadField label="个人UOC数量" value={tab1.uoc as string} />
                      <ReadField label="个人FC币数量" value={tab1.fc as string} />
                      <ReadField label="个人MP币数量" value={tab1.mp as string} />
                      <ReadField label="个人AP币数量" value={tab1.ap as string} />
                      <ReadField label="个人激活码数量" value={tab1.ac as string} />
                      <div style={{ borderTop: '1px solid #eee', margin: '16px 0' }} />
                      <ReadField
                        label="直推人数"
                        value={
                          tab1.direct_count != null ? String(tab1.direct_count) : '—'
                        }
                      />
                      <ReadField label="直属UO" value={tab1.team_uo as string} />
                      <ReadField label="直属EP" value={tab1.team_ep as string} />
                      <ReadField label="直属EP1" value={tab1.team_ep1 as string} />
                      <ReadField label="直属EP2" value={tab1.team_ep2 as string} />
                      <ReadField label="直属RP" value={tab1.team_rp as string} />
                      <ReadField label="直属SP1" value={tab1.team_sp1 as string} />
                      <ReadField label="直属SP2" value={tab1.team_sp2 as string} />
                      <ReadField label="直属SP3" value={tab1.team_sp3 as string} />
                      <ReadField label="直属TP" value={tab1.team_tp as string} />
                      <ReadField label="直属UOC" value={tab1.team_uoc as string} />
                      <ReadField label="直属FC" value={tab1.team_fc as string} />
                      <ReadField label="直属MP" value={tab1.team_mp as string} />
                      <ReadField label="直属AP" value={tab1.team_ap as string} />
                      <ReadField label="直属激活码" value={tab1.team_ac as string} />
                    </>
                  ) : null}
                </div>
              ),
            },
            {
              key: 't2',
              label: '直接推荐人查询',
              children: (
                <div style={{ maxWidth: 720 }}>
                  <AccountSearchForm
                    onSearch={(a) => search2Mut.mutate(a)}
                    loading={search2Mut.isPending}
                  />
                  {tab2 ? (
                    <>
                      <ReadField label="个人编号ID" value={tab2.number as string} />
                      <ReadField label="个人账号用户名" value={tab2.account as string} />
                      <ReadField label="直推人ID号" value={tab2.push_number as string} />
                      <ReadField label="直推人用户名" value={tab2.push_account as string} />
                      <ReadField label="直推人昵称" value={tab2.push_nickname as string} />
                      <ReadField label="直推人注册时间" value={tab2.push_time as string} />
                      <ReadField label="直推人账户级别" value={tab2.push_level as string} />
                    </>
                  ) : null}
                </div>
              ),
            },
            {
              key: 't3',
              label: '节点人查询',
              children: (
                <div style={{ maxWidth: 720 }}>
                  <AccountSearchForm
                    onSearch={(a) => search4Mut.mutate(a)}
                    loading={search4Mut.isPending}
                  />
                  {tab3Node ? (
                    <>
                      <ReadField label="个人编号ID" value={tab3Node.number as string} />
                      <ReadField label="个人账号用户名" value={tab3Node.account as string} />
                      <ReadField label="节点人ID号" value={tab3Node.push_number as string} />
                      <ReadField label="节点人用户名" value={tab3Node.push_account as string} />
                      <ReadField label="节点人昵称" value={tab3Node.push_nickname as string} />
                      <ReadField label="节点人注册时间" value={tab3Node.push_time as string} />
                      <ReadField label="节点人账户级别" value={tab3Node.push_level as string} />
                    </>
                  ) : null}
                </div>
              ),
            },
            {
              key: 't4',
              label: 'RP/TP查询',
              children: (
                <div>
                  <Form
                    layout="inline"
                    style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
                    onFinish={(v) => {
                      const q = {
                        account: String(v.account).trim(),
                        coin: v.coin as 'rp' | 'tp',
                        time: String(v.time),
                      };
                      setRpQuery(q);
                      setRpPage(1);
                      rpLogMut.mutate({ ...q, page: 1 });
                    }}
                  >
                    <Form.Item name="account" label="编号/账号" rules={[{ required: true }]}>
                      <Input style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="coin" label="币种" initialValue="rp">
                      <Select
                        style={{ width: 90 }}
                        options={[
                          { value: 'rp', label: 'RP' },
                          { value: 'tp', label: 'TP' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name="time" label="时间" initialValue="0">
                      <Select
                        style={{ width: 120 }}
                        options={[
                          { value: '0', label: '全部' },
                          { value: '1', label: '近1个月' },
                          { value: '3', label: '近3个月' },
                          { value: '6', label: '近6个月' },
                          { value: '12', label: '近12个月' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={rpLogMut.isPending}>
                        查询
                      </Button>
                    </Form.Item>
                  </Form>
                  <Table
                    size="small"
                    rowKey="id"
                    columns={rpColumns}
                    dataSource={rpLog?.rows ?? []}
                    loading={rpLogMut.isPending}
                    pagination={{
                      current: rpPage,
                      total: rpLog?.total ?? 0,
                      pageSize: 20,
                      showTotal: (t) => `共 ${t} 条`,
                      onChange: (page) => {
                        if (!rpQuery) return;
                        setRpPage(page);
                        rpLogMut.mutate({ ...rpQuery, page });
                      },
                    }}
                    scroll={{ x: 1100 }}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </ConfigProvider>
  );
}

export default ParityUserSearchPage;
